import {
  faChevronDown,
  faChevronRight,
  faLock,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';

import ContractCoverageIndicator from '@/components/ContractCoverageIndicator';
import ContractGridFloatingFilter from '@/components/ContractGridFloatingFilter';
import type { Props as FloatingFilterProps } from '@/components/ContractGridFloatingFilter/types';
import ContractInsuranceEntitiesPanel from '@/components/ContractInsuranceEntitiesPanel';
import { CONTRACTS_PATH } from '@/constants/contracts';
import ContractsGridContext from '@/contexts/contractsGridContext';
import type { Contract, ContractChoice, ContractStatus } from '@/types/contract';
import { Pill, type PillProps, Tooltip, Typography } from '@/ui';
import { formatContractDate, formatContractMonth } from '@/utils/contractLabels';

export interface ContractDetailRow {
  id: string;
  detailOf: Contract;
}

export type ContractGridRow = Contract | ContractDetailRow;

export const isDetailRow = (row: ContractGridRow | undefined): row is ContractDetailRow =>
  !!row && 'detailOf' in row;

// Mirrors the ordering fields in backend/homebase/contracts/filters/contract_filter.py.
export const SORTABLE_FIELDS = new Set(['start_at', 'end_at', 'created_at']);
export const DEFAULT_ORDERING = '-start_at';

const STATUS_PILLS: Record<ContractStatus, { label: string; color: PillProps['color'] }> = {
  active: { label: 'Active', color: 'primary' },
  upcoming: { label: 'Upcoming', color: 'secondary' },
  expired: { label: 'Expired', color: 'neutral' }
};

type CellParams = ICellRendererParams<ContractGridRow>;

const ExpandCell = ({ data }: CellParams) => {
  const { expandedIds, toggleExpanded } = useContext(ContractsGridContext);
  if (!data || isDetailRow(data)) return null;
  const expanded = expandedIds.has(data.id);
  return (
    <button
      type="button"
      aria-label={expanded ? 'Collapse contract' : 'Expand contract'}
      aria-expanded={expanded}
      className="flex h-full w-full items-center justify-center text-uniform-gray-500 hover:text-uniform-gray-800"
      onClick={() => toggleExpanded(data.id)}>
      <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
    </button>
  );
};

// Rendered for the full-width row under an expanded contract. Defined once here so AG Grid
// keeps the same component across renders instead of remounting (and refetching) the panel.
export const ContractDetailCell = ({ data, node, api }: ICellRendererParams<ContractGridRow>) => {
  const { filterOptions } = useContext(ContractsGridContext);
  const updateHeight = useCallback(
    (height: number) => {
      if (height > 0 && node.rowHeight !== height) {
        node.setRowHeight(height);
        api.onRowHeightChanged();
      }
    },
    [node, api]
  );
  if (!isDetailRow(data)) return null;
  return (
    <ContractInsuranceEntitiesPanel
      contract={data.detailOf}
      filterOptions={filterOptions}
      onHeightChange={updateHeight}
    />
  );
};

const conflictsTooltip = (contract: Contract) => (
  <div className="max-w-xs space-y-1">
    {contract.conflicts.map((conflict) => (
      <Typography
        key={`${conflict.contract_id}-${conflict.insurance_provider_name}-${conflict.insurance_type}-${conflict.jurisdiction_abbreviation}`}
        className="text-inherit dark:text-inherit">
        Contracted twice: {conflict.insurance_provider_name} · {conflict.insurance_type} ·{' '}
        {conflict.jurisdiction_abbreviation}. Also covered by {conflict.contract_name} from{' '}
        {formatContractDate(conflict.overlap_start)}.
      </Typography>
    ))}
  </div>
);

const contractCellRenderer = ({ data }: CellParams) => {
  if (!data || isDetailRow(data)) return null;
  return (
    <div className="flex h-full w-full items-center justify-between gap-2">
      <div className="min-w-0 leading-tight">
        <Typography weight="semibold" className="truncate leading-tight">
          <Link to={`${CONTRACTS_PATH}/${data.id}`} className="hover:underline">
            {data.name}
          </Link>
          {data.locked_at && (
            <FontAwesomeIcon
              icon={faLock}
              title="Locked"
              className="ml-2 text-xs text-uniform-gray-500"
            />
          )}
        </Typography>
        {data.organization_name && (
          <Typography variant="caption" as="p" muted className="truncate">
            {data.organization_name}
          </Typography>
        )}
      </div>
      {data.conflicts.length > 0 && (
        <Tooltip content={conflictsTooltip(data)} focusable>
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            aria-label="Contracted twice"
            className="text-orange-600"
          />
        </Tooltip>
      )}
    </div>
  );
};

const statusCellRenderer = ({ data }: CellParams) => {
  if (!data || isDetailRow(data)) return null;
  const { label, color } = STATUS_PILLS[data.status];
  return (
    <Pill size="sm" variant="subtle" color={color} className="inline leading-none">
      {label}
    </Pill>
  );
};

const termCellRenderer = ({ data }: CellParams) => {
  if (!data || isDetailRow(data)) return null;
  return (
    <span>
      {formatContractMonth(data.start_at)} →{' '}
      {data.end_at ? (
        formatContractMonth(data.end_at)
      ) : (
        <Tooltip content="Open ended" focusable>
          …
        </Tooltip>
      )}
    </span>
  );
};

const coverageTooltip = (contract: Contract, category: ContractChoice) => (
  <div className="space-y-0.5">
    <Typography weight="medium" className="text-inherit dark:text-inherit">
      {category.label} — insurance entities covering each family:
    </Typography>
    {(contract.category_coverage[category.value]?.families ?? []).map((family) => (
      <Typography key={family.program_family_id} className="text-inherit dark:text-inherit">
        {family.program_family_name}: {family.fully_covered_insurance_entities}/
        {contract.insurance_entity_count}
        {family.covered_insurance_entities > family.fully_covered_insurance_entities &&
          ` (${family.covered_insurance_entities - family.fully_covered_insurance_entities} partly)`}
      </Typography>
    ))}
  </div>
);

const coverageColumn = (category: ContractChoice): ColDef<ContractGridRow> => ({
  colId: `coverage_${category.value}`,
  headerName: category.label,
  sortable: false,
  minWidth: 120,
  flex: 1,
  cellRenderer: ({ data }: CellParams) => {
    if (!data || isDetailRow(data)) return null;
    return (
      <div className="flex h-full w-full items-center">
        <Tooltip content={coverageTooltip(data, category)} focusable>
          <ContractCoverageIndicator
            coverage={data.category_coverage[category.value]?.coverage ?? 'none'}
          />
        </Tooltip>
      </div>
    );
  }
});

// Columns with these get a floating filter row cell that writes to the list's query params.
const floatingFilter = (params: FloatingFilterProps): Partial<ColDef<ContractGridRow>> => ({
  filter: true,
  floatingFilter: true,
  suppressHeaderFilterButton: true,
  suppressFloatingFilterButton: true,
  floatingFilterComponent: ContractGridFloatingFilter,
  floatingFilterComponentParams: params
});

export const createColumnDefs = (categories: ContractChoice[]): ColDef<ContractGridRow>[] => [
  {
    colId: 'expand',
    headerName: '',
    width: 44,
    sortable: false,
    resizable: false,
    cellRenderer: ExpandCell
  },
  {
    colId: 'contract',
    headerName: 'Contract',
    flex: 2,
    minWidth: 220,
    sortable: false,
    cellRenderer: contractCellRenderer,
    ...floatingFilter({ kind: 'text', param: 'name', placeholder: 'Filter…' })
  },
  {
    colId: 'version',
    headerName: 'Version',
    width: 110,
    sortable: false,
    cellClass: 'flex items-center',
    valueGetter: ({ data }) =>
      data && !isDetailRow(data) ? `v${data.version} of ${data.version_count}` : ''
  },
  {
    colId: 'status',
    headerName: 'Status',
    width: 130,
    sortable: false,
    cellClass: 'flex items-center',
    cellRenderer: statusCellRenderer,
    ...floatingFilter({
      kind: 'choice',
      optionsKey: 'statuses',
      param: 'status',
      placeholder: 'Status'
    })
  },
  {
    colId: 'start_at',
    field: 'start_at' as never,
    headerName: 'Term',
    width: 190,
    cellClass: 'flex items-center',
    cellRenderer: termCellRenderer,
    ...floatingFilter({ kind: 'date', param: 'covers', placeholder: 'Covers date…' })
  },
  ...categories.map(coverageColumn)
];
